import { useMemo } from 'react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import SearchSelect, { type SearchSelectOption } from '@/components/Shared/SearchSelect';
import { CategoryBadge } from '@/pages/Import/components/CategoryBadge';
import { useContractImportStore } from '@/store/useContractImportStore';
import { ContractStepFooter } from './ContractStepFooter';
import { Field, INPUT, Kicker, ModeToggle } from './wizardUi';

export function ObjectStep() {
  const { t } = useTranslation('contractImport');
  const { extraction, object, objectMatch, customer, setObject, live } = useContractImportStore();

  // When the customer is linked, only that customer's objects are valid link targets
  // (the BE rejects an object that belongs to another customer).
  const options: SearchSelectOption[] = useMemo(() => {
    const pool =
      customer.mode === 'link' && customer.existingCustomerId
        ? live.objects.filter((o) => o.customerId === customer.existingCustomerId)
        : live.objects;
    return pool.map((o) => ({ id: o.id, label: o.name, description: o.street }));
  }, [live.objects, customer.mode, customer.existingCustomerId]);

  const suggested: SearchSelectOption[] = useMemo(() => {
    if (!objectMatch.suggestion) return [];
    const stillValid = options.some((o) => o.id === objectMatch.suggestion!.id);
    return stillValid
      ? [{ id: objectMatch.suggestion.id, label: objectMatch.suggestion.name, description: objectMatch.suggestion.street }]
      : [];
  }, [objectMatch.suggestion, options]);

  const extracted = extraction?.object;

  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-4">
          <p className="text-[13px] text-slate-600">{t('object.hint')}</p>

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1">
            <Kicker>{t('object.extracted')}</Kicker>
            <div className="text-[13px] text-slate-900">{extracted?.name || '—'}</div>
            <div className="text-[12px] text-slate-500">
              {[extracted?.street, [extracted?.zip, extracted?.city].filter(Boolean).join(' '), extracted?.country]
                .filter(Boolean)
                .join(' · ') || '—'}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <ModeToggle mode={object.mode} onChange={(mode) => setObject({ mode })} />
            <CategoryBadge category={objectMatch.category} score={objectMatch.score} />
          </div>

          {object.mode === 'link' ? (
            <SearchSelect
              options={options}
              suggestedOptions={suggested}
              value={object.existingObjectId}
              onChange={(id) => setObject({ existingObjectId: id })}
              placeholder={t('object.pickObject')}
            />
          ) : (
            <div className="space-y-3">
              <Field label={t('object.fields.name')}>
                <input className={INPUT} value={object.name} onChange={(e) => setObject({ name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_0.6fr_1fr] gap-3">
                <Field label={t('object.fields.street')}>
                  <input className={INPUT} value={object.street} onChange={(e) => setObject({ street: e.target.value })} />
                </Field>
                <Field label={t('object.fields.zip')}>
                  <input className={INPUT} value={object.zip} onChange={(e) => setObject({ zip: e.target.value })} />
                </Field>
                <Field label={t('object.fields.city')}>
                  <input className={INPUT} value={object.city} onChange={(e) => setObject({ city: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('object.fields.country')}>
                  <input className={INPUT} value={object.country} onChange={(e) => setObject({ country: e.target.value })} />
                </Field>
              </div>
            </div>
          )}
        </div>
      </Card>

      <ContractStepFooter back="customer" next="positions" />
    </div>
  );
}

export default ObjectStep;
